import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';

interface FormValues {
  password: string;
  confirm_password: string;
}

interface InviteDetails {
  email: string;
  role_name: string | null;
  expires_at: string;
}

/**
 * Public landing page for an emailed back-office invitation. The token in the
 * query string is the only credential — it is validated server-side before the
 * password form is shown, so an expired or revoked link fails fast instead of
 * after the user has typed a password.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    if (!token) {
      setLoadError('This invitation link is missing its token.');
      setLoading(false);
      return;
    }

    let alive = true;
    (async () => {
      try {
        const res = await api.get<InviteDetails>(
          `/back-office/admins/invite/${encodeURIComponent(token)}`
        );
        if (alive) setInvite(res.data);
      } catch (err: unknown) {
        if (alive) {
          setLoadError(
            extractErrorMessage(err, 'This invitation link is not valid')
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token]);

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await api.post(
        `/back-office/admins/invite/${encodeURIComponent(token)}/accept`,
        { password: values.password }
      );
      setDone(true);
      // Deliberately no auto sign-in: the new admin proves the password works
      // by using it, which also seeds the browser password manager.
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: unknown) {
      setServerError(extractErrorMessage(err, 'Could not accept invitation'));
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-full items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow">
        {children}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <p className="text-sm text-slate-500">Checking invitation…</p>
    );
  }

  if (loadError) {
    return shell(
      <>
        <h1 className="text-2xl font-semibold text-slate-800">
          Invitation unavailable
        </h1>
        <div className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </div>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="mt-6 w-full rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-600"
        >
          Go to sign in
        </button>
      </>
    );
  }

  if (done) {
    return shell(
      <>
        <h1 className="text-2xl font-semibold text-slate-800">
          You&apos;re all set
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Your password has been set. Redirecting you to sign in…
        </p>
      </>
    );
  }

  return shell(
    <form onSubmit={handleSubmit(onSubmit)}>
      <h1 className="text-2xl font-semibold text-slate-800">
        Set your password
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        You&apos;ve been invited to the SwiftMedix back office as{' '}
        <span className="font-medium text-slate-700">
          {invite?.role_name ?? 'an administrator'}
        </span>
        .
      </p>
      <p className="mt-3 rounded bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {invite?.email}
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Min 8 characters' },
              validate: {
                lower: (v) =>
                  /[a-z]/.test(v) || 'Must contain a lowercase letter',
                upper: (v) =>
                  /[A-Z]/.test(v) || 'Must contain an uppercase letter',
                digit: (v) => /[0-9]/.test(v) || 'Must contain a number',
              },
            })}
          />
          {errors.password && (
            <p className="mt-1 text-xs text-red-600">
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">
            Confirm password
          </label>
          <input
            type="password"
            autoComplete="new-password"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            {...register('confirm_password', {
              required: 'Please confirm your password',
              validate: (v) =>
                v === watch('password') || 'Passwords do not match',
            })}
          />
          {errors.confirm_password && (
            <p className="mt-1 text-xs text-red-600">
              {errors.confirm_password.message}
            </p>
          )}
        </div>
      </div>

      {serverError && (
        <div className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-600 disabled:opacity-50"
      >
        {submitting ? 'Setting password…' : 'Set password'}
      </button>
    </form>
  );
}
